type SpanUnit = "year" | "month" | "day" | "hour" | "minute" | "second";

const JP_LABEL: Record<SpanUnit, string> = {
  year: "年",
  month: "か月",
  day: "日",
  hour: "時間",
  minute: "分",
  second: "秒",
};

/**
 * Parse "YYYY-MM-DD HH:MM:SS" as a local-time Date.
 * Returns null if invalid.
 */
function parseLocalDateTime(input: string): Date | null {
  // Accept exactly "YYYY-MM-DD HH:MM:SS" (allow leading/trailing spaces)
  const m = input.trim().match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})$/
  );
  if (!m) return null;

  const year = Number(m[1]);
  const monthIndex = Number(m[2]) - 1; // JS months 0-11
  const day = Number(m[3]);
  const hour = Number(m[4]);
  const minute = Number(m[5]);
  const second = Number(m[6]);

  const d = new Date(year, monthIndex, day, hour, minute, second);

  // Validate round-trip to reject things like 2026-02-31 10:00:00
  if (
    d.getFullYear() !== year ||
    d.getMonth() !== monthIndex ||
    d.getDate() !== day ||
    d.getHours() !== hour ||
    d.getMinutes() !== minute ||
    d.getSeconds() !== second
  ) {
    return null;
  }

  return d;
}

function daysInMonth(year: number, monthIndex: number): number {
  // monthIndex: 0-11
  return new Date(year, monthIndex + 1, 0).getDate();
}

function clampDayToMonth(year: number, monthIndex: number, day: number): number {
  return Math.min(day, daysInMonth(year, monthIndex));
}

/**
 * Add months to a Date while trying to preserve the day-of-month.
 * If the target month has fewer days, clamps to the last day.
 */
function addMonths(base: Date, monthsToAdd: number): Date {
  const y = base.getFullYear();
  const m = base.getMonth();
  const d = base.getDate();

  const totalMonths = m + monthsToAdd;
  const newYear = y + Math.floor(totalMonths / 12);
  const newMonth = ((totalMonths % 12) + 12) % 12;

  const newDay = clampDayToMonth(newYear, newMonth, d);

  const result = new Date(base);
  result.setFullYear(newYear);
  result.setMonth(newMonth);
  result.setDate(newDay);
  return result;
}

function addYears(base: Date, yearsToAdd: number): Date {
  const result = new Date(base);
  const targetYear = base.getFullYear() + yearsToAdd;
  const targetMonth = base.getMonth();
  const targetDay = clampDayToMonth(targetYear, targetMonth, base.getDate());

  result.setFullYear(targetYear);
  result.setMonth(targetMonth);
  result.setDate(targetDay);
  return result;
}

function formatParts(parts: Array<{ unit: SpanUnit; value: number }>, unitCount: number): string {
  const nonZero = parts.filter((p) => p.value > 0);
  if (nonZero.length === 0) return `0${JP_LABEL.second}`;

  const first = nonZero[0];
  const second = nonZero[1];

  const firstStr = `${first.value}${JP_LABEL[first.unit]}`;

  if (unitCount === 1) return firstStr;

  if (!second) return firstStr;
  return `${firstStr} ${second.value}${JP_LABEL[second.unit]}`;
}

/**
 * Returns a human readable span in Japanese of the difference between `input`
 * and now, using the top 1–2 significant units (down to seconds).
 *
 * Examples: "2日 3時間", "3分 22秒", "4年 4か月", "2か月"
 */
export default function humanReadableSpan(input: string, unitCount = 2, now: Date = new Date()): string {
  const target = parseLocalDateTime(input);
  if (!target) {
    return "";
  }

  // Use absolute difference regardless of past/future
  let from = target;
  let to = now;
  if (from.getTime() > to.getTime()) {
    const tmp = from;
    from = to;
    to = tmp;
  }

  // Build a "calendar-aware" diff for years/months/days,
  // then remainder for hours/minutes/seconds.
  let cursor = new Date(from);

  // Years
  let years = to.getFullYear() - cursor.getFullYear();
  let test = addYears(cursor, years);
  if (test.getTime() > to.getTime()) {
    years -= 1;
    test = addYears(cursor, years);
  }
  cursor = test;

  // Months
  let months =
    (to.getFullYear() - cursor.getFullYear()) * 12 + (to.getMonth() - cursor.getMonth());
  test = addMonths(cursor, months);
  if (test.getTime() > to.getTime()) {
    months -= 1;
    test = addMonths(cursor, months);
  }
  cursor = test;

  // Remaining milliseconds -> days/h/m/s
  let remainingMs = to.getTime() - cursor.getTime();
  const secondMs = 1000;
  const minuteMs = 60 * secondMs;
  const hourMs = 60 * minuteMs;
  const dayMs = 24 * hourMs;

  const days = Math.floor(remainingMs / dayMs);
  remainingMs -= days * dayMs;

  const hours = Math.floor(remainingMs / hourMs);
  remainingMs -= hours * hourMs;

  const minutes = Math.floor(remainingMs / minuteMs);
  remainingMs -= minutes * minuteMs;

  const seconds = Math.floor(remainingMs / secondMs);

  // Pick top 2 significant units (in order), omit second if 0
  const parts: Array<{ unit: SpanUnit; value: number }> = [
    { unit: "year", value: years },
    { unit: "month", value: months },
    { unit: "day", value: days },
    { unit: "hour", value: hours },
    { unit: "minute", value: minutes },
    { unit: "second", value: seconds },
  ];

  return formatParts(parts, unitCount);
}
